import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useMotionPreferences } from "../hooks/use-motion-preferences";
import "./HeroD.css";

/* ────────────────────────────────────────────────────────────────
   Hero D — Editorial Corporate, text-left / photo-right split.
   Robert Half / Korn Ferry composition adapted for Adel Group.
   Cream surface, Fraunces H1, equal-weight dual CTAs, single
   accent word, magazine-cadence motion.
   ──────────────────────────────────────────────────────────────── */

function WordReveal({ children, delay = 0, accent = false }) {
  const words = String(children).split(" ");
  return (
    <span style={{ display: "inline" }}>
      {words.map((w, i) => (
        <span key={i} className="hero-d__word-clip">
          <motion.span
            className={`hero-d__word ${accent ? "hero-d__word--accent" : ""}`}
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.7,
              delay: delay + i * 0.07,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

export default function HeroD() {
  const { prefersReducedMotion } = useMotionPreferences();
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section className="hero-d">
      <div className="hero-d__container">
        <div className="hero-d__content">
          <motion.span
            className="hero-d__eyebrow"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05, ease }}
          >
            Kenya &rarr; the Gulf &middot; Since 2015
          </motion.span>

          <h1 className="hero-d__title">
            <span className="hero-d__title-line">
              <WordReveal delay={0.18}>Connecting Talents.</WordReveal>
            </span>
            <span className="hero-d__title-line">
              <WordReveal delay={0.40}>Creating</WordReveal>{" "}
              <span className="hero-d__word-clip">
                <motion.span
                  className="hero-d__word hero-d__word--accent"
                  initial={prefersReducedMotion ? false : { y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.55, ease }}
                >
                  Opportunities.
                </motion.span>
              </span>
            </span>
          </h1>

          <motion.p
            className="hero-d__sub"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease }}
          >
            Helping employers find the right talent and candidates the right
            job&mdash;every step of recruitment.
          </motion.p>

          <motion.div
            className="hero-d__ctas"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.1, ease }}
          >
            <Link to="/contact" className="hero-d__cta hero-d__cta--primary">
              Hire talent
              <ArrowRight size={16} strokeWidth={2} />
            </Link>
            <Link to="/job-seekers" className="hero-d__cta hero-d__cta--ghost">
              Find work
              <ArrowUpRight size={16} strokeWidth={2} />
            </Link>
          </motion.div>

          <motion.div
            className="hero-d__rule"
            initial={prefersReducedMotion ? false : { scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1.2, delay: 1.3, ease }}
            style={{ transformOrigin: "left" }}
          />

          <motion.dl
            className="hero-d__microbar"
            initial={prefersReducedMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.45, ease }}
          >
            <div>
              <dt>Placed</dt>
              <dd>5,000+ careers</dd>
            </div>
            <div>
              <dt>Trusted by</dt>
              <dd>50+ Gulf employers</dd>
            </div>
            <div>
              <dt>Across</dt>
              <dd>17 industries</dd>
            </div>
          </motion.dl>
        </div>

        <motion.figure
          className="hero-d__figure"
          initial={prefersReducedMotion ? false : { opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease }}
        >
          <img
            src="/industries/construction-infrastructure.jpg"
            alt="Adel Group placed Kenyan workforce on a Gulf infrastructure project"
            className="hero-d__image"
            loading="eager"
            decoding="async"
          />
          <figcaption className="hero-d__caption">
            <div className="hero-d__caption-quote">
              &ldquo;48 hospitality crew deployed to a UAE chain&mdash;in 6 weeks.&rdquo;
            </div>
            <div className="hero-d__caption-attr">
              Adel placement &middot; Hospitality &middot; UAE
            </div>
          </figcaption>
        </motion.figure>
      </div>
    </section>
  );
}
